// Cache formatter instances to avoid recreating Intl.NumberFormat on every call
const formatterCache = new Map<string, Intl.NumberFormat>();

export const SUPPORTED_LOCALES = [
  'es',
  'en',
  'pt',
  'ca',
  'eu',
  'gl',
  'es-AR',
  'es-CO',
  'es-MX',
  'es-ES',
  'es-CR',
  'es-PA',
  'es-NI',
  'en-US',
  'en-GB',
  'pt-BR',
] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

// Mapping from app-internal codes to valid BCP 47 tags.
// Note: 'mx' and 'vl' are NOT included here because they are not valid
// SupportedLocale values — they must be converted via mapLanguageToLocale()
// before reaching formatNumber.
// CAS countries (es-CR, es-PA, es-NI) are mapped to 'es' because CLDR assigns
// dot-decimal to those subtags (US influence) but client requirement is comma decimal.
const LOCALE_MAP: Partial<Record<SupportedLocale, string>> = {
  eu: 'eu-ES',
  gl: 'gl-ES',
  ca: 'ca-ES',
  'es-CR': 'es',
  'es-PA': 'es',
  'es-NI': 'es',
};

export function resolveLocaleForNumberFormat(locale: SupportedLocale): string {
  return LOCALE_MAP[locale] ?? locale;
}

function getFormatter(locale: SupportedLocale, options?: Intl.NumberFormatOptions): Intl.NumberFormat {
  const resolvedLocale = resolveLocaleForNumberFormat(locale);
  const key = options
    ? `${resolvedLocale}_${JSON.stringify(
        Object.keys(options)
          .sort((a, b) => a.localeCompare(b))
          .reduce<Intl.NumberFormatOptions>((acc, k) => ({ ...acc, [k]: options[k as keyof Intl.NumberFormatOptions] }), {})
      )}`
    : resolvedLocale;
  if (!formatterCache.has(key)) {
    formatterCache.set(key, new Intl.NumberFormat(resolvedLocale, options));
  }
  return formatterCache.get(key) as Intl.NumberFormat;
}

/**
 * Formats a number according to the user's locale using Intl.NumberFormat.
 * Caches formatter instances to avoid the cost of repeated instantiation.
 *
 * @param value - Number to format (always in internal format with decimal point)
 * @param locale - Locale supported by the application (BCP 47)
 * @param options - Additional Intl.NumberFormatOptions (optional)
 * @returns Formatted string according to the locale, or '' if value is NaN or not finite
 */
export function formatNumber(value: number, locale: SupportedLocale, options?: Intl.NumberFormatOptions): string {
  if (!Number.isFinite(value)) return '';
  return getFormatter(locale, options).format(value);
}
